import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { showPersistentError } from '../../lib/errorMessages';
import { logger } from '../../lib/logger';
import { SystemError } from '../../lib/errors';

function getStatusCode(error: unknown): number | undefined {
  if (error instanceof SystemError) return error.statusCode;
  return undefined;
}

// 403/404 on a background query means the resource legitimately isn't there for this user — not an error to surface.
function handleQueryError(error: unknown) {
  if (getStatusCode(error) !== undefined) {
    logger.warn('Suppressed background query error', { message: (error as Error)?.message });
    return;
  }
  const err = error instanceof Error ? error : new Error('An unexpected error occurred');
  // showPersistentError detects ApiValidationError and shows backend messages verbatim;
  // all other errors are translated through getFriendlyError() before display.
  const referenceId = showPersistentError(err);
  logger.warn('React Query error', { message: err.message, referenceId });
}

// Mutations are user-triggered, so surface their errors — except 403, which belongs to a permission gate, not an error card.
function handleMutationError(error: unknown) {
  if (getStatusCode(error) === 403) {
    logger.warn('Permission denied (403) on mutation', { message: (error as Error)?.message });
    return;
  }
  const err = error instanceof Error ? error : new Error('An unexpected error occurred');
  const referenceId = showPersistentError(err);
  logger.warn('React Query mutation error', { message: err.message, referenceId });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleMutationError,
  }),
  defaultOptions: {
    queries: {
      staleTime: 0, // 5 minutes
      retry: 1,
    },
  },
});

interface QueryProviderProps {
  children: ReactNode;
}

export const QueryProvider = ({ children }: QueryProviderProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
};